import React from "react";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";

import { useCourierOnboarding } from "features/courier-onboarding/context/CourierOnboardingContext";
import FormDatePicker from "shared/components/forms/FormDatePicker";
import FormInput from "shared/components/forms/FormInput";

interface Props {
  errors: Record<string, string>;
  /** Fired when a contact field changes, so the host can clear a stale duplicate warning. */
  onContactChange?: () => void;
}

/**
 * Identity and contact fields of the merged data step. Presentational: it
 * writes straight into the onboarding context and renders the error keys the
 * host step resolved, so all validation and saving stays in one place.
 */
const PersonalFields: React.FC<Props> = ({ errors, onContactChange }) => {
  const { t } = useTranslation();
  const { data, setPersonal, setMetadata } = useCourierOnboarding();
  const p = data.personalInfo;

  const tError = (key: string | undefined): string | undefined =>
    key ? (t(key) as string) : undefined;

  return (
    <>
      <section className="flex flex-col gap-4">
        <h2 className="text-sm font-medium text-gray-700">
          {t("onboarding.personal.identitySection")}
        </h2>
        <FormInput
          id="field-fullName"
          fieldKey="fullName"
          label={t("onboarding.personal.fullName")}
          placeholder={t("onboarding.personal.fullNamePlaceholder") ?? ""}
          value={p.fullName}
          onChange={(e) => setPersonal({ fullName: e.target.value })}
          error={tError(errors.fullName)}
          required
        />
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <FormInput
            id="field-taxId"
            fieldKey="taxId"
            label={t("onboarding.personal.taxId")}
            placeholder={t("onboarding.personal.taxIdPlaceholder") ?? ""}
            value={p.taxId}
            onChange={(e) => {
              onContactChange?.();
              setPersonal({
                taxId: e.target.value.replace(/\D/g, "").slice(0, 9),
              });
            }}
            error={tError(errors.taxId)}
            inputMode="numeric"
            maxLength={9}
            required
          />
          <FormDatePicker
            id="field-dateOfBirth"
            fieldKey="dateOfBirth"
            label={t("onboarding.personal.dateOfBirth")}
            value={p.dateOfBirth}
            onChange={(value) => setPersonal({ dateOfBirth: value })}
            error={tError(errors.dateOfBirth)}
            minDate={dayjs().subtract(100, "year")}
            maxDate={dayjs().subtract(16, "year")}
            required
          />
        </div>
      </section>

      <section className="flex flex-col gap-4 border-t border-gray-200 pt-5">
        <h2 className="text-sm font-medium text-gray-700">
          {t("onboarding.personal.contactSection")}
        </h2>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <FormInput
            id="field-phoneNumber"
            fieldKey="phoneNumber"
            label={t("onboarding.personal.phoneNumber")}
            placeholder={t("onboarding.personal.phoneNumberPlaceholder") ?? ""}
            value={p.phoneNumber}
            onChange={(e) => {
              onContactChange?.();
              const next = e.target.value;
              setPersonal({ phoneNumber: next });
              if (
                data.metadata.phoneVerified ||
                data.metadata.phoneVerificationToken ||
                data.metadata.otpChallengeId
              ) {
                if (data.metadata.verifiedPhone === next) return;
                setMetadata({
                  phoneVerified: false,
                  verifiedPhone: undefined,
                  phoneVerificationToken: undefined,
                  otpChallengeId: undefined,
                  otpSentTo: undefined,
                });
              }
            }}
            error={tError(errors.phoneNumber)}
            inputMode="tel"
            maxLength={10}
            required
          />
          <FormInput
            id="field-email"
            fieldKey="email"
            label={t("onboarding.personal.email")}
            placeholder={t("onboarding.personal.emailPlaceholder") ?? ""}
            value={p.email}
            onChange={(e) => setPersonal({ email: e.target.value })}
            error={tError(errors.email)}
            type="email"
            required
          />
        </div>
      </section>
    </>
  );
};

export default PersonalFields;
